<script>

  import { mapState } from 'vuex'
  import { gsap } from 'gsap'
  import isRelativeUrl from '@/mixins/isRelativeUrl'
  import { gtmSendEvent } from '@/utils/gtm-send-event'

  export default {
    mixins: [isRelativeUrl],
    data () {
      return {
        scrollPosition: 0,
        activeSubMenu: null
      }
    },
    computed: {
      ...mapState({
        menuOpen: state => state.menuOpen,
        mainNavigation: state => state.mainNavigation
      })
    },
    watch: {
      menuOpen (isOpen) {
        if (isOpen && typeof window !== 'undefined') {
          this.scrollPosition = window.pageYOffset
          const $body = document.querySelector('body')
          const bodyPaddingRight = `${window.innerWidth - document.body.clientWidth}px`
          $body.style.paddingRight = bodyPaddingRight
          $body.style.overflow = 'hidden'
          $body.style.position = 'fixed'
          $body.style.top = `-${this.scrollPosition}px`
          $body.style.width = '100%'
        }
        if (!isOpen && typeof window !== 'undefined') {
          const $body = document.querySelector('body')
          $body.style.removeProperty('padding-right')
          $body.style.removeProperty('overflow')
          $body.style.removeProperty('position')
          $body.style.removeProperty('top')
          $body.style.removeProperty('width')
          window.scrollTo(0, this.scrollPosition)
        }
      }
    },
    methods: {
      isActiveLink (link) {
        const decodedFullPath = decodeURIComponent(this.$route.fullPath)
        const decodedLink = decodeURIComponent(this.toRelativeUrl(link))

        return decodedFullPath === decodedLink
      },
      handleSubmenuClick (id) {
        this.activeSubMenu = this.activeSubMenu === id ? null : id
      },
      enter (el, done) {
        const tl = gsap.timeline({ onComplete: done })
        tl.from(el, {
          y: '-100%',
          duration: 0.25,
          ease: 'expo.out'
        })
      },
      leave (el, done) {
        const tl = gsap.timeline({ onComplete: done })
        tl.to(el, {
          y: '-100%',
          duration: 0.2,
          ease: 'expo.in',
          clearProps: 'all'
        })
      },
      trackEventLogin () {
        gtmSendEvent('header-mobile', 'mijn-examenbundel', 'clicked')
      },
      trackEventMain (item) {
        gtmSendEvent('navigation-mobile', 'click-main-item', item.fields.title)
      },
      trackEventSub (parent, child) {
        gtmSendEvent('navigation-mobile', 'click-sub-item', parent.fields.title + ' / ' + child.fields.title)
      }
    }
  }
</script>

<template>
  <transition
    :css="false"
    @enter="enter"
    @leave="leave"
  >
    <div
      v-show="menuOpen"
      class="mobile-navigation z-1000"
    >
      <ul v-if="mainNavigation.fields">
        <li>
          <nuxt-link
            to="/"
            class="nav-link main-nav-link"
            @click.native="trackEventMain( { fields: { title: 'Home' } })"
          >
            Home
          </nuxt-link>
        </li>
        <li
          v-for="item in mainNavigation.fields.navigationItems"
          :key="item.sys.id"
        >
          <button
            v-if="item.fields.childItems"
            class="nav-link main-nav-link"
            :class="{'submenu-open': activeSubMenu === item.sys.id}"
            @click="handleSubmenuClick(item.sys.id)"
          >
            {{ item.fields.title }}
            <span class="text-blue-500">
              <q-icon
                v-if="item.fields.childItems"
                name="chevron-right-blue"
              />
            </span>
          </button>
          <div v-else>
            <nuxt-link
              v-if="isRelativeUrl(item.fields.link)"
              :to="toRelativeUrl(item.fields.link)"
              class="nav-link main-nav-link"
              :class="{ 'text-blue-500': isActiveLink(item.fields.link) }"
              active-class=""
              exact-active-class=""
              @click.native="trackEventMain(item)"
            >
              {{ item.fields.title }}
            </nuxt-link>
            <a
              v-else
              :href="item.fields.link"
              target="_blank"
              class="nav-link main-nav-link"
            >
              {{ item.fields.title }}
            </a>
          </div>
          <ul
            v-show="item.fields.childItems"
            class="sub-nav"
            :class="{'sub-nav-open': activeSubMenu === item.sys.id}"
          >
            <li class="border-b nav-link sub-nav-link back-link">
              <button
                type="button"
                class="flex w-full items-center"
                @click="handleSubmenuClick()"
              >
                <span class="mr-3 text-blue-500 ">
                  <q-icon
                    v-if="item.fields.childItems"
                    name="chevron-left"
                  /></span>
                Terug
              </button>
            </li>
            <li
              v-for="child in item.fields.childItems"
              :key="child.sys.id"
            >
              <nuxt-link
                v-if="isRelativeUrl(child.fields.link)"
                :to="toRelativeUrl(child.fields.link)"
                class="nav-link sub-nav-link"
                :class="{ 'text-blue-500': isActiveLink(child.fields.link) }"
                active-class=""
                exact-active-class=""
                @click.native="trackEventSub(item, child)"
              >
                {{ child.fields.title }}
              </nuxt-link>
              <a
                v-else
                :href="child.fields.link"
                target="_blank"
                rel="noopener noreferrer"
                class="nav-link sub-nav-link"
              >
                {{ child.fields.title }}
              </a>
            </li>
          </ul>
        </li>
      </ul>
      <e-button
        tag="nuxt-link"
        to="/mijnexamenbundel"
        aria-label="Mijn Examenbundel"
        type="primary"
        class="my-login"
        @click.native="trackEventLogin()"
      >
        <q-icon name="profile" />
        <span class="block ml-2">
          Mijn Examenbundel
        </span>
      </e-button>
    </div>
  </transition>
</template>

<style lang="scss">
.mobile-navigation {
  @apply fixed left-0 top-0 w-full h-full z-30 bg-white overflow-y-auto;
  padding-top: 6.125rem;

  ul {
    @apply px-4
  }

  li {
    @apply bg-white;
  }

  li:not(:last-child) {
    .nav-link {
      @apply border-b border-gray-200;
    }
  }

  .nav-link {
    @apply relative flex w-full items-center outline-none text-black py-6 justify-between text-lg leading-6;

    &.nuxt-link-exact-active {
      @apply text-blue-500;
    }

    .q-icon {
      transition: transform 0.25s ease(easeOutSine);
    }

    &.submenu-open {
      .q-icon {
        transform: rotate(180deg);
      }
    }
  }

  .sub-nav-link {
    /* @apply pl-12; */
  }

  .icon-chevron-right-blue svg {
    height: 14px;
    width: 8px ;
  }

  .sub-nav {
      @apply transition-transform duration-500 absolute translate-x-full top-0 right-0 w-full h-full bg-white overflow-y-auto;
      transform: translateX(100%);
      padding-top: 6.125rem;
  }

  .sub-nav-open.sub-nav {
    @apply z-30;
    transform: translateX(0);
  }

  .back-link, .back-link span {
    @apply flex justify-start items-center

  }

  .my-login {
    @apply mx-4 mt-6;
  }
}
</style>
